/**
 * Imperative confirmAsync() helper — Promise-based confirmation for mid-flow
 * code paths that previously used `window.confirm()`.
 *
 * Usage:
 *   1. Mount <ConfirmHost /> once near the app root.
 *   2. import { confirmAsync } from '../lib/confirmAsync';
 *   3. `if (!await confirmAsync({ title: 'Delete?', variant: 'destructive' })) return;`
 *
 * Returns a Promise<boolean>. Resolves true on confirm, false on cancel/close.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog';

// Module-level resolver; set by ConfirmHost on mount. Multiple ConfirmHost
// instances would clobber each other — that's why we mount exactly one at
// the App root.
let _show = null;

export const confirmAsync = (opts) =>
  new Promise((resolve) => {
    if (!_show) {
      // Graceful fallback: if ConfirmHost isn't mounted yet (e.g. very early
      // app boot), defer to window.confirm so we never strand the caller.

      resolve(window.confirm(opts?.description || opts?.title || 'Confirm?'));
      return;
    }
    _show({ ...opts, _resolve: resolve });
  });

export const ConfirmHost = () => {
  const [state, setState] = useState(null);
  // Refs guard against late onOpenChange callbacks resolving twice.
  const resolverRef = useRef(null);

  useEffect(() => {
    _show = (opts) => {
      resolverRef.current = opts._resolve;
      setState(opts);
    };
    return () => {
      _show = null;
    };
  }, []);

  const onOpenChange = useCallback((open) => {
    if (!open) {
      // Closed without confirming → false.
      resolverRef.current?.(false);
      resolverRef.current = null;
      setState(null);
    }
  }, []);

  const onConfirm = useCallback(() => {
    resolverRef.current?.(true);
    resolverRef.current = null;
    // ConfirmDialog already calls onOpenChange(false) after onConfirm; clear here.
    setState(null);
  }, []);

  if (!state) return null;

  return (
    <ConfirmDialog
      open={!!state}
      onOpenChange={onOpenChange}
      title={state.title}
      description={state.description}
      confirmLabel={state.confirmLabel || 'Confirm'}
      cancelLabel={state.cancelLabel || 'Cancel'}
      variant={state.variant}
      onConfirm={onConfirm}
      testIdPrefix={state.testIdPrefix || 'global-confirm'}
    />
  );
};

export default confirmAsync;
